// Tecu class example.
// let tecu = new Tecu('192.168.1.2:80')
// tecu.getInfo({ onDownloadProgress }) -> promise yielding json-decoded object
// tecu.getFaultLog({ partial, onDownloadProgress }) -> promise yielding arraybuffer
// tecu.getRecorderLog({ partial, onDownloadProgress }) -> promise yielding arraybuffer
// tecu.startStreaming({ shortTerm, onMetadataProgress, onPacketReceived }) -> promise yielding empty when stream closed
// tecu.openStreamingLog({ onDownloadProgress }) -> promise yielding arraybuffer
// tecu.closeStreamingLog()
// tecu.uploadBuild({ buildFile, onUploadProgress }) -> promise yielding json-decoded object
// tecu.finalizeUpload({ bldInfo }) -> promise.
// tecu.cancel() -> undefined

import 'core-js/stable'
import axios from 'axios'
// import encoding from 'text-encoding'

import { parseBuffer, TecuDataPacket } from './TecuParser'

const decoder = new TextDecoder('latin1')
const timeout_ms = 10000

class BadStatusError {}
class NoDataError extends BadStatusError {}
class TimeoutError {}
class CancelledError { }

const headers = { 'X-File-Explorer': '0' }

function saveArrayBuffer(buf, saveAs) {
  let el = document.createElement('A')
  el.style.display = 'none'
  el.href = URL.createObjectURL(new Blob([buf], { type: 'octet/stream' }))
  el.download = saveAs
  document.body.appendChild(el)
  el.click()
  document.body.removeChild(el)
}

export default class TecuApi {

  constructor (baseUrl) {
    this.baseUrl = baseUrl
    this.state = TecuApi.IDLE
    this.__cancel = null
    this.__streamingDownload = null
    axios.defaults.timeout = 180000
  }

  async getInfo(o) {
    let { onDownloadProgress } = o || {}
    this.state = TecuApi.DOWNLOADING
    return await this.__axios({
      url: this.baseUrl + '/info',
      responseType: 'json',
      onDownloadProgress,
      timeout: 15000,
    })
  }

  async getFaultLog(o) {
    if (!o) o = {}
    o.url = this.baseUrl + '/logs/tfl'
    return await this.__getDownload(o)
  }

  async getRecorderLog(o) {
    if (!o) o = {}
    o.url = this.baseUrl + '/logs/tdr'
    return await this.__getDownload(o)
  }

  async getStatistics(o) {
    if (!o) o = {}
    o.url = this.baseUrl + '/logs/tstat'
    let metadata = await this.__getDownload(o)
    let datasets = parseBuffer(metadata)
    //console.log(datasets)
    let pkt = datasets[0].d.buf.slice(datasets[0].d.pack_ranges[0][0],datasets[0].d.pack_ranges[0][1])
    //console.log(pkt)
    return new TecuDataPacket(datasets[0].d, pkt)
  }

  //setup streaming and return the metadata structure
  async initStreaming(onMetadataProgress) {
    let metadata = await this.__getDownload({
      url: this.baseUrl + '/live/trt/meta',
      onDownloadProgress: onMetadataProgress,
    })
    // console.log("meta", metadata);
    return parseBuffer(metadata)[0].d
  }


  //May be used for an alternate to initStreaming.
  async getMeta(onMetadataProgress) {
    let metadata = await this.__getDownload({
      url: this.baseUrl + '/live/trt/meta',
      onDownloadProgress: onMetadataProgress,
    })
    return {
      parsedData: parseBuffer(metadata)[0].d,
      metaData: metadata,
    }
  }
  //getMeta... parse buffer.
  // async initStreaming(onMetadataProgress) {
  //   let metadata = await this.__getDownload({
  //     url: this.baseUrl + '/live/trt/meta',
  //     onDownloadProgress: onMetadataProgress,
  //   })
  //   // console.log("meta", metadata);
  //   return parseBuffer(metadata)[0].d
  // }

  async startStreaming({ shortTerm, onMetadataProgress, onPacketReceived: opr }) {
    let ds = await this.initStreaming(onMetadataProgress)
    return this.__streamPackets({
      url: `${this.baseUrl}/live/trt/data${shortTerm?'?shortterm':''}`,
      packetSize: ds.pkt_size,
      onPacketReceived: (pkt) => {
        // TODO: pass pkt to streaming log
        if (opr) opr(new TecuDataPacket(ds, pkt.buffer))
      },
    })

    // META: /live/trt/meta
    // DATA: /live/trt/data OR /live/trt/data?shortterm
    //throw new Error('not implemented')
  }
  openStreamingLog({ onDownloadProgress }) { throw new Error('not implemented') }
  closeStreamingLog() { throw new Error('not implemented') }
  async uploadBuild({ file, onUploadProgress }) {
    this.state = TecuApi.UPLOADING
    return await this.__axios({
      url: `${this.baseUrl}/sw?upload`,
      method: 'POST',
      responseType: 'json',
      onUploadProgress,
      data: file,
    })
  }
  async finalizeUpload({ bldInfo }) {
    this.state = TecuApi.UPLOADING
    return await this.__axios({
      url: `${this.baseUrl}/sw?finalize`,
      method: 'POST',
      responseType: 'json',
      data: bldInfo,
    })
  }
  async streamDirectory({ path, onItemReceived }) {
    this.state = TecuApi.DOWNLOADING
    let rv = null
    await this.__streamLines({
      url: `${this.baseUrl}/ff?${path}`,
      onLineReceived: (line) => {
        const o = JSON.parse(line)
        if ('rc' in o) rv = o
        else onItemReceived(o)
      },
    })
    if (rv && rv.rc === 0)
      return rv
    else
      throw rv
  }
  async getSDCardFile({ path, onDownloadProgress, saveAs }) {
    return await this.__getDownload({
      url: `${this.baseUrl}/ff?${path}`,
      onDownloadProgress,
      saveAs,
    })
  }

  cancel(mes) {
    if (this.__cancel) {
      let c = this.__cancel
      this.__cancel = null
      c(mes)
    }
  }

  // Private methods.

  async __getDownload({ url, partial, onDownloadProgress, saveAs }) {
    this.state = TecuApi.DOWNLOADING
    if (partial) url += '?partial'
    let buf = await this.__axios({
      url,
      responseType: 'arraybuffer',
      onDownloadProgress,
      timeout: 600000,
    })
    if (saveAs)
      saveArrayBuffer(buf, saveAs)
    if (partial) {
      this.state = TecuApi.ACKING
      try {
        await this.__axios({
          url: url + 'Ack',
          responseType: 'arraybuffer',
        })
      } catch(e) {
        throw new Error('Failed to acknowledge the download.')
      }
    }
    return buf
  }

  async __axios(o) {
    this.cancel("TECU API: axios, new request to " + o.url)
    let { onDownloadProgress: odp, onUploadProgress: oup } = o,
      upPartial = false, downPartial = false
    let timeout, timeoutFn = () => {
      timeout = null
      this.cancel("TECU API: axios, timeout cancel " + o.url)
    }
    o.cancelToken = new axios.CancelToken(c => { this.__cancel = c })
    o.onDownloadProgress = e => {
      if (timeout)
        clearTimeout(timeout)
      downPartial = e.total && e.loaded < e.total
      timeout = (downPartial || upPartial) ? setTimeout(timeoutFn, timeout_ms) : null
      if (odp)
        odp(e)
    }
    o.onUploadProgress = e => {
      if (timeout)
        clearTimeout(timeout)
      upPartial = e.total && e.loaded < e.total
      timeout = (downPartial || upPartial) ? setTimeout(timeoutFn, timeout_ms) : null
      if (oup)
        oup(e)
    }
    o.headers = headers
    timeout = setTimeout(timeoutFn, timeout_ms)
    try {
      return (await axios(o)).data
    } catch(e) {
      if (timeout === null)
        throw new TimeoutError()
      if (e.response && e.response.status === 404)
        throw new NoDataError()
      if (axios.isCancel(e)) {
        if(e.message) e.message = "connection canceled to " + o.url + " Reason: " + e.message
        else e.message = "connection canceled to " + o.url
        //console.log("connection canceled")//throw new CancelledError()
      }
      throw e
    } finally {
      if (timeout)
        clearTimeout(timeout)
      this.state = TecuApi.IDLE
    }
  }

  async __streamPackets({ url, packetSize, onPacketReceived }) {
    this.cancel("TECU API: streamPackets, new request to " + url)
    const aborter = new AbortController()
    let canceled = false
    this.__cancel = () => { canceled = true; aborter.abort() }
    let response = await fetch(url, { signal: aborter.signal, headers })
    try {
      const reader = response.body.getReader()
      let q = [], qsize = 0
      for (;;) {
        let { done, value: chunk } = await reader.read()
        if (done) break
        q.push(chunk)
        qsize += chunk.length
        for (;;) {
          if (qsize < packetSize)
            break
          chunk = q.shift()
          if (chunk.length > packetSize) {
            // The first chunk has more than enough data for an entire packet.
            // Slice out the packet and send it to the listener.
            onPacketReceived(chunk.slice(0, packetSize))
            q.unshift(chunk.slice(packetSize))
          } else if (chunk.length === packetSize) {
            // The first chunk contains exactly one packet.
            // Send it to the listener.
            onPacketReceived(chunk)
          } else {
            // The packet is spread out over several chunks.
            // Piece the chunks together and send the resulting packet to the
            // listener.
            let arr = new Uint8Array(packetSize), n = 0, remaining = packetSize
            for (;;) {
              arr.set(chunk, n)
              n += chunk.length
              remaining -= chunk.length
              chunk = q.shift()
              if (chunk.length > remaining) {
                arr.set(chunk.slice(0, remaining), n)
                q.unshift(chunk.slice(remaining))
                break
              } else if (chunk.length === remaining) {
                arr.set(chunk, n)
                break
              }
            }
            onPacketReceived(arr)
          }
          qsize -= packetSize
        }
      }
    } catch(e) {
      if (canceled)
        console.log("connection canceled")//throw new CancelledError()
      else throw e
    }
  }

  async __streamLines({ url, onLineReceived }) {
    this.cancel("TECU API: streamLines, new request to " + url)
    const aborter = new AbortController()
    let canceled = false
    this.__cancel = () => { canceled = true; aborter.abort() }
    let response = await fetch(url, { signal: aborter.signal, headers })
    try {
      const reader = response.body.getReader()
      let buf = ''
      for (;;) {
        let { done, value: chunk } = await reader.read()
        if (done) break
        buf += decoder.decode(chunk)
        for (;;) {
          const i = buf.indexOf('\n')
          if (i < 0) break
          onLineReceived(buf.substring(0, i))
          buf = buf.substring(i + 1)
        }
      }
      if (buf)
        onLineReceived(buf)
    } catch(e) {
      if (canceled)
        throw new CancelledError()
      throw e
    }
  }

}

TecuApi.BadStatusError = BadStatusError
TecuApi.NoDataError = NoDataError
TecuApi.TimeoutError = TimeoutError
TecuApi.CancelledError = CancelledError

TecuApi.IDLE = 0
TecuApi.DOWNLOADING = 1
TecuApi.STREAMING = 2
TecuApi.ACKING = 3
TecuApi.UPLOADING = 4

global.TecuApi = TecuApi
